/**
 * Claude Agent SDK Adapter — implements SdkAdapter for @anthropic-ai/claude-agent-sdk
 *
 * Uses the `query()` function from the Claude Agent SDK to run Claude as a
 * stateful coding agent with tool execution (Read, Edit, Bash, etc.).
 *
 * Supports:
 * - send()          — start a new query session and return the result text
 * - followUp()      — resume the same session for output-format corrections
 * - sendExecution() — rich path using full contract context
 *
 * The SDK returns an AsyncGenerator of SDKMessage events. The adapter iterates
 * the stream, extracts the final result text from the SDKResultSuccess message,
 * and captures the session_id for followUp via `resume`.
 *
 * GuardrailHooks are mapped to the SDK's PreToolUse hook system.
 */

import type {
  SdkAdapter,
  AdapterSendOptions,
  AgentExecutionRequest,
  AgentProgressEvent,
  CacheConfig,
  CandidateAgent,
} from "../lib/task-runner.js";
import type { GuardrailHooks } from "../lib/guardrail-hooks.js";
import type { MemoryRef } from "../lib/memory-store.js";

/**
 * Subset of the Claude Agent SDK `AgentDefinition` shape we populate.
 * Verified against node_modules/@anthropic-ai/claude-agent-sdk/sdk.d.ts:38
 * (`description`, `prompt`, optional `tools`, `model`).
 */
type ClaudeAgentDefinition = {
  description: string;
  prompt: string;
  tools?: string[];
  model?: string;
};

// ---------------------------------------------------------------------------
// Duck-typed SDK types (avoid hard compile-time dependency on zod v4)
// ---------------------------------------------------------------------------

type SDKResultSuccess = {
  type: "result";
  subtype: "success";
  result: string;
  session_id: string;
};

type SDKResultError = {
  type: "result";
  subtype: "error";
  error: string;
  session_id: string;
};

type SDKContentBlock = {
  type: string;
  name?: string;
  text?: string;
  input?: unknown;
};

type SDKBetaMessage = {
  content?: SDKContentBlock[];
};

type SDKMessage = {
  type: string;
  subtype?: string;
  result?: string;
  error?: string;
  session_id?: string;
  tool_name?: string;
  summary?: string;
  message?: SDKBetaMessage;
};

type HookCallbackInput = {
  hook_event_name: string;
  tool_name: string;
  tool_input: unknown;
  [key: string]: unknown;
};

type HookCallbackOutput = {
  hookEventName: string;
  permissionDecision?: "allow" | "deny" | "ask";
  permissionDecisionReason?: string;
  additionalContext?: string;
};

type HookCallbackMatcher = {
  tool_name?: string;
  hooks: Array<(input: HookCallbackInput) => HookCallbackOutput | Promise<HookCallbackOutput>>;
};

type QueryOptions = Record<string, unknown>;

type QueryFn = (params: {
  prompt: string;
  options?: QueryOptions;
}) => AsyncGenerator<SDKMessage, void>;

// ---------------------------------------------------------------------------
// Guardrail hook bridge
// ---------------------------------------------------------------------------

function buildClaudeHooks(
  guardrails: GuardrailHooks,
): Partial<Record<string, HookCallbackMatcher[]>> {
  const preToolUseHook: HookCallbackMatcher = {
    hooks: [
      (input: HookCallbackInput): HookCallbackOutput => {
        const toolInput = input.tool_input as Record<string, unknown> | undefined;
        const toolName = input.tool_name;

        if (toolName === "Bash" || toolName === "Shell") {
          const command = (toolInput?.command ?? toolInput?.input) as string | undefined;
          if (command) {
            const result = guardrails.beforeShellExecution({ command });
            if (result.permission === "deny") {
              return {
                hookEventName: "PreToolUse",
                permissionDecision: "deny",
                permissionDecisionReason: result.user_message ?? result.agent_message,
              };
            }
            if (result.additionalContext) {
              return { hookEventName: "PreToolUse", additionalContext: result.additionalContext };
            }
          }
        }

        const filePath = (toolInput?.file_path ?? toolInput?.path) as string | undefined;
        if (filePath) {
          const pathResult = guardrails.preToolUse({
            tool_name: toolName,
            tool_input: { file_path: filePath },
          });
          if (pathResult.permission === "deny") {
            return {
              hookEventName: "PreToolUse",
              permissionDecision: "deny",
              permissionDecisionReason: pathResult.user_message ?? pathResult.agent_message,
            };
          }
          if (pathResult.additionalContext) {
            return { hookEventName: "PreToolUse", additionalContext: pathResult.additionalContext };
          }
        }

        return { hookEventName: "PreToolUse" };
      },
    ],
  };

  return { PreToolUse: [preToolUseHook] };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ClaudeAgentSdkAdapterConfig {
  /** Working directory for the agent. Defaults to process.cwd(). */
  cwd?: string;
  /** Model identifier (e.g. "claude-sonnet-4-20250514"). Uses SDK default if omitted. */
  model?: string;
  /**
   * Available tools. Defaults to read-only or read-write set based on AdapterSendOptions.
   * Set to `{ type: 'preset', preset: 'claude_code' }` for all Claude Code tools.
   */
  tools?: string[] | { type: "preset"; preset: "claude_code" };
  /** Permission mode. Defaults to "bypassPermissions" for automated workflows. */
  permissionMode?: "default" | "acceptEdits" | "bypassPermissions" | "plan";
  /** Max turns before stopping. */
  maxTurns?: number;
  /** Runtime guardrail hooks enforced via PreToolUse SDK hooks. */
  guardrailHooks?: GuardrailHooks;
  /** Prompt caching configuration. Enabled by default. */
  cacheConfig?: CacheConfig;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export class ClaudeAgentSdkAdapter implements SdkAdapter {
  private cwd: string;
  private model: string | undefined;
  private tools: string[] | { type: "preset"; preset: "claude_code" } | undefined;
  private permissionMode: string;
  private maxTurns: number | undefined;
  private guardrailHooks: GuardrailHooks | undefined;
  private cacheConfig: CacheConfig;
  private lastSessionId: string | null = null;
  private lastMemoryRef: MemoryRef | null = null;
  private queryFn: QueryFn | null = null;

  constructor(config: ClaudeAgentSdkAdapterConfig = {}) {
    this.cwd = config.cwd ?? process.cwd();
    this.model = config.model;
    this.tools = config.tools;
    this.permissionMode = config.permissionMode ?? "bypassPermissions";
    this.maxTurns = config.maxTurns;
    this.guardrailHooks = config.guardrailHooks;
    this.cacheConfig = config.cacheConfig ?? { enabled: true };
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  private async resolveQueryFn(): Promise<QueryFn> {
    if (this.queryFn) return this.queryFn;

    const sdk = (await import("@anthropic-ai/claude-agent-sdk")) as {
      query: QueryFn;
    };
    this.queryFn = sdk.query;
    return this.queryFn;
  }

  private buildOptions(
    readonly: boolean,
    resume?: string,
    systemPrompt?: string[],
    agents?: CandidateAgent[],
  ): QueryOptions {
    const opts: QueryOptions = {
      cwd: this.cwd,
      permissionMode: this.permissionMode,
      allowDangerouslySkipPermissions: this.permissionMode === "bypassPermissions",
      // SDK isolation: do not load ambient .claude/ settings, skills, or agents
      // from disk. The runtime injects everything in-code, so disk discovery
      // would break component isolation and reproducibility.
      settingSources: [],
    };

    if (this.model) opts.model = this.model;
    if (this.maxTurns) opts.maxTurns = this.maxTurns;

    let toolList: string[] | { type: "preset"; preset: "claude_code" };
    if (this.tools) {
      toolList = Array.isArray(this.tools) ? [...this.tools] : this.tools;
    } else {
      toolList = readonly
        ? ["Read", "Glob", "Grep"]
        : ["Read", "Glob", "Grep", "Edit", "Write", "Bash"];
    }

    // When candidate sub-agents are registered (model B), the entry agent needs
    // the sub-agent delegation tool to invoke them. The restricted tool list
    // above omits it, so add it explicitly — otherwise the LLM has no way to
    // delegate. The SDK names this tool "Task"/"Agent" (docs use both; tool_use
    // events surface it as "Agent"), so allow both for robustness across
    // SDK versions. (A "preset" tools config already includes it.)
    if (agents && agents.length > 0 && Array.isArray(toolList)) {
      for (const t of ["Task", "Agent"]) {
        if (!toolList.includes(t)) toolList.push(t);
      }
    }
    opts.tools = toolList;

    if (resume) opts.resume = resume;

    if (systemPrompt && this.cacheConfig.enabled !== false) {
      opts.systemPrompt = systemPrompt;
    }

    // Register candidate sub-agents so the LLM can delegate via the Agent/Task
    // tool (model B, LLM-internal routing). Keyed by candidate name.
    if (agents && agents.length > 0) {
      const record: Record<string, ClaudeAgentDefinition> = {};
      for (const a of agents) {
        record[a.name] = {
          description: a.description,
          prompt: a.prompt,
          ...(a.tools ? { tools: a.tools } : {}),
          ...(a.model ? { model: a.model } : {}),
        };
      }
      opts.agents = record;
    }

    if (this.guardrailHooks) {
      opts.hooks = buildClaudeHooks(this.guardrailHooks);
    }

    return opts;
  }

  private async runQuery(
    prompt: string,
    options: QueryOptions,
    onProgress?: (event: AgentProgressEvent) => void,
  ): Promise<{ text: string; sessionId: string }> {
    const queryFn = await this.resolveQueryFn();
    const stream = queryFn({ prompt, options });

    let resultText = "";
    let sessionId = "";

    for await (const message of stream) {
      if (message.session_id) sessionId = message.session_id;
      if (message.type === "result") {
        if (message.subtype === "success") {
          resultText = (message as SDKResultSuccess).result;
        } else if (message.subtype === "error") {
          throw new Error(`Claude Agent SDK error: ${(message as SDKResultError).error}`);
        }
      } else if (onProgress) {
        emitProgressFromSdkMessage(message, onProgress, sessionId);
      }
    }

    return { text: resultText, sessionId };
  }

  // -------------------------------------------------------------------------
  // SdkAdapter interface
  // -------------------------------------------------------------------------

  async send(
    prompt: string,
    options: AdapterSendOptions,
  ): Promise<string> {
    this.lastSessionId = null;
    this.lastMemoryRef = null;

    const split = options.splitPrompt;
    const systemPrompt = split?.system;
    const userPrompt = split ? split.user : prompt;
    const opts = this.buildOptions(options.readonly, undefined, systemPrompt, options.agents);
    const { text, sessionId } = await this.runQuery(userPrompt, opts, options.onProgress);

    this.lastSessionId = sessionId;
    this.lastMemoryRef = {
      id: sessionId,
      provider: "claude-agent-sdk",
      compat: "claude-agent-sdk@0.2",
      created_at: new Date().toISOString(),
    };
    return text;
  }

  async followUp(message: string): Promise<string> {
    if (!this.lastSessionId) {
      throw new Error("followUp() called before send() — no active session");
    }
    const opts = this.buildOptions(false, this.lastSessionId);
    const { text, sessionId } = await this.runQuery(message, opts);
    this.lastSessionId = sessionId;
    return text;
  }

  async sendExecution(request: AgentExecutionRequest): Promise<string> {
    this.lastSessionId = null;
    this.lastMemoryRef = null;

    const resume = request.memoryRef?.id;
    const split = request.splitPrompt;
    const systemPrompt = split?.system;
    const userPrompt = split ? split.user : request.prompt;
    const agents = request.options.agents ?? request.agents;
    const opts = this.buildOptions(request.options.readonly, resume, systemPrompt, agents);
    const { text, sessionId } = await this.runQuery(userPrompt, opts, request.options.onProgress);

    this.lastSessionId = sessionId;
    this.lastMemoryRef = {
      id: sessionId,
      provider: "claude-agent-sdk",
      compat: "claude-agent-sdk@0.2",
      created_at: new Date().toISOString(),
      parent_run_id: request.memoryRef?.id,
    };
    return text;
  }

  getLastMemoryRef(): MemoryRef | null {
    return this.lastMemoryRef;
  }

  isCompatible(compat: string): boolean {
    return compat.startsWith("claude-agent-sdk@");
  }

  // -------------------------------------------------------------------------
  // Test support
  // -------------------------------------------------------------------------

  /**
   * Inject a custom query function for testing.
   * Bypasses the dynamic import of @anthropic-ai/claude-agent-sdk.
   */
  static withQueryFn(
    queryFn: QueryFn,
    config?: ClaudeAgentSdkAdapterConfig,
  ): ClaudeAgentSdkAdapter {
    const adapter = new ClaudeAgentSdkAdapter(config);
    adapter.queryFn = queryFn;
    return adapter;
  }
}

// ---------------------------------------------------------------------------
// Progress event mapping
// ---------------------------------------------------------------------------

function emitProgressFromSdkMessage(
  message: SDKMessage,
  onProgress: (event: AgentProgressEvent) => void,
  sessionId?: string,
): void {
  const sid = message.session_id ?? sessionId;
  switch (message.type) {
    case "assistant": {
      const content = message.message?.content;
      if (!content) break;
      for (const block of content) {
        if (block.type === "tool_use" && block.name) {
          const input = block.input && typeof block.input === "object"
            ? block.input as Record<string, unknown>
            : undefined;
          onProgress({ type: "tool_use", tool_name: block.name, input, session_id: sid });
        } else if (block.type === "text" && block.text) {
          onProgress({ type: "text", message: block.text, session_id: sid });
        }
      }
      break;
    }
    case "tool_progress":
      if (message.tool_name) {
        onProgress({ type: "tool_use", tool_name: message.tool_name, session_id: sid });
      }
      break;
    case "tool_use_summary":
      if (message.summary) {
        onProgress({ type: "status", message: message.summary, session_id: sid });
      }
      break;
    default:
      break;
  }
}
